import { useRequest } from 'ahooks';
import { useEffect, useState } from 'react';

import { coreServiceUrl } from '../../../../config/environment';
import { sortingBy } from '../../../../utils/arrays/sortingBy';
import { callApi } from '../../../../utils/callApi';

const THREE_HOURS = 1000 * 60 * 60 * 3;

export enum ColorDefaultUsage {
  PRIMARY_DEFAULT = 'PRIMARY_DEFAULT',
  SECONDARY_DEFAULT = 'SECONDARY_DEFAULT',
  NONE = 'NONE',
}

export const useColorApi = () => {
  /* libs hooks */
  const { data: colorValues } = useRequest(getColorValues, {
    cacheKey: 'wsc/colors/values',
    cacheTime: THREE_HOURS,
    staleTime: THREE_HOURS,
  });

  /* local states */
  const [colorByRalValue, setColorByRalValue] = useState<Record<string, ColorValueDto>>(
    groupByRalValue(colorValues),
  );
  const [primaryColor, setPrimaryColor] = useState<ColorValueDto>();
  const [secondaryColor, setSecondaryColor] = useState<ColorValueDto>();

  /* update states values */
  useEffect(() => {
    if (colorValues) {
      setColorByRalValue(groupByRalValue(colorValues));
      setPrimaryColor(
        colorValues.find((value) => value.defaultUsage === ColorDefaultUsage.PRIMARY_DEFAULT),
      );
      setSecondaryColor(
        colorValues.find((value) => value.defaultUsage === ColorDefaultUsage.SECONDARY_DEFAULT),
      );
    }
  }, [colorValues]);

  return { colorValues, colorByRalValue, primaryColor, secondaryColor };
};

export type ColorValueDto = {
  displayOrder: number;
  ralValue: string;
  defaultUsage: string;
  name: string;
};

export const hexColorByRalValue: Record<string, string> = {
  'RAL 9002': '#D7D5CB',
  'RAL 7035': '#C5C7C4',
  'RAL 7005': '#6C6E6B',
  'RAL 7016': '#383E42',
  'RAL 6011': '#6C7C59',
  'RAL 5018': '#058B8C',
  'RAL 5012': '#0089B6',
  'RAL 5011': '#1A2B3C',
  'RAL 5005': '#005387',
  'RAL 3003': '#861A22',
};

const getColorValues = async (): Promise<ColorValueDto[]> => {
  const url = `${coreServiceUrl}/wsc/colors/values`;
  const { data } = await callApi<ColorValueDto[]>(url, 'GET');
  return data.sort(sortingBy('displayOrder'));
};

const groupByRalValue = (values: ColorValueDto[] = []): Record<string, ColorValueDto> => {
  return values.reduce((acc, item) => {
    acc[item.ralValue] = item;
    return acc;
  }, {});
};
